/**
 * Aufbau der Seitenleiste (N-01) - rein und getestet.
 *
 * Die Standardgruppen stehen hier; was Ole aendert (Namen, Reihenfolge,
 * Ausblenden, Favoriten, Einklappen) liegt als eine kleine Einstellung in der
 * Tabelle `oberflaeche` und wird synchronisiert. Die Einstellung speichert nur
 * Abweichungen: Kommt ein Eintrag neu dazu, erscheint er automatisch am Ende
 * seiner Gruppe, und ein unbekannter Schluessel (aus einer neueren Version)
 * wird still ignoriert.
 *
 * Die Gruppe "bereiche" ist dynamisch (alle Bereiche) und hat keine festen
 * Eintraege; Einstellungen stehen immer ganz unten und lassen sich nicht
 * ausblenden.
 */

export type GruppenId = 'heute' | 'bereiche' | 'richtung' | 'ablage';

export interface NavEintrag {
  id: string;
  to: string;
  label: string;
  end?: boolean;
}

export interface NavGruppe {
  id: GruppenId;
  titel: string;
  eintraege: readonly NavEintrag[];
}

export const STANDARD_GRUPPEN: readonly NavGruppe[] = [
  { id: 'heute', titel: 'Heute', eintraege: [
    { id: 'uebersicht', to: '/', label: 'Übersicht', end: true },
    { id: 'kalender', to: '/kalender', label: 'Kalender' },
    { id: 'aufgaben', to: '/aufgaben', label: 'Aufgaben' },
    { id: 'gewohnheiten', to: '/gewohnheiten', label: 'Gewohnheiten' },
  ] },
  { id: 'bereiche', titel: 'Bereiche', eintraege: [] },
  { id: 'richtung', titel: 'Richtung & Reflexion', eintraege: [
    { id: 'ziele', to: '/ziele', label: 'Ziele' },
    { id: 'projekte', to: '/projekte', label: 'Projekte' },
    { id: 'journal', to: '/journal', label: 'Journal' },
    { id: 'rueckblick', to: '/rueckblick', label: 'Rückblick' },
    { id: 'statistik', to: '/statistik', label: 'Statistik' },
  ] },
  { id: 'ablage', titel: 'Ablage', eintraege: [
    { id: 'listen', to: '/listen', label: 'Listen' },
    { id: 'wissen', to: '/wissen', label: 'Wissen' },
    { id: 'notizen', to: '/ressourcen', label: 'Notizen & Gedanken' },
  ] },
];

/** Was gespeichert wird. Alles optional: leer = Standard. */
export interface SeitenleistenEinstellung {
  id: 'seitenleiste';
  /** Reihenfolge der Gruppen. */
  gruppenReihenfolge?: GruppenId[];
  /** Eigene Gruppennamen. */
  titel?: Partial<Record<GruppenId, string>>;
  /** Reihenfolge der Eintraege je Gruppe. */
  eintragReihenfolge?: Partial<Record<GruppenId, string[]>>;
  /** Ausgeblendete Eintraege. */
  versteckt?: string[];
  /** Angeheftete Eintraege (stehen oben unter "Favoriten"). */
  favoriten?: string[];
  /** Eingeklappte Gruppen. */
  eingeklappt?: GruppenId[];
  /** Nur Symbole zeigen (Icon-Leiste). */
  schmal?: boolean;
  updatedAt: string;
}

export const LEERE_EINSTELLUNG: SeitenleistenEinstellung = { id: 'seitenleiste', updatedAt: '' };

const GRUPPEN_IDS = STANDARD_GRUPPEN.map((g) => g.id);
const ALLE_EINTRAEGE = new Map(STANDARD_GRUPPEN.flatMap((g) => g.eintraege.map((e) => [e.id, e] as const)));

const liste = (roh: unknown): string[] => (Array.isArray(roh) ? roh.filter((x): x is string => typeof x === 'string') : []);

/** Gespeicherten Wert pruefen (Systemgrenze: kommt aus der Datenbank bzw. vom Sync). */
export function einstellungAus(roh: unknown): SeitenleistenEinstellung {
  if (typeof roh !== 'object' || roh === null) return LEERE_EINSTELLUNG;
  const r = roh as Record<string, unknown>;
  const gruppe = (x: string): x is GruppenId => (GRUPPEN_IDS as string[]).includes(x);
  const titel: Partial<Record<GruppenId, string>> = {};
  if (typeof r.titel === 'object' && r.titel !== null) {
    for (const [k, v] of Object.entries(r.titel)) if (gruppe(k) && typeof v === 'string' && v.trim()) titel[k] = v.trim().slice(0, 40);
  }
  const reihenfolge: Partial<Record<GruppenId, string[]>> = {};
  if (typeof r.eintragReihenfolge === 'object' && r.eintragReihenfolge !== null) {
    for (const [k, v] of Object.entries(r.eintragReihenfolge)) if (gruppe(k)) reihenfolge[k] = liste(v);
  }
  return {
    id: 'seitenleiste',
    gruppenReihenfolge: liste(r.gruppenReihenfolge).filter(gruppe),
    titel,
    eintragReihenfolge: reihenfolge,
    versteckt: liste(r.versteckt),
    favoriten: liste(r.favoriten),
    eingeklappt: liste(r.eingeklappt).filter(gruppe),
    schmal: r.schmal === true,
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : '',
  };
}

export interface AufgeloesteGruppe {
  id: GruppenId;
  titel: string;
  eingeklappt: boolean;
  eintraege: { eintrag: NavEintrag; versteckt: boolean; favorit: boolean }[];
}

/** Die Seitenleiste, wie sie jetzt aussieht - mit Ausgeblendeten (fuer den Anpassen-Modus). */
export function aufloesen(e: SeitenleistenEinstellung): AufgeloesteGruppe[] {
  const eigene = (e.gruppenReihenfolge ?? []).filter((id, i, a) => a.indexOf(id) === i);
  const reihenfolge = [...eigene, ...GRUPPEN_IDS.filter((id) => !eigene.includes(id))];
  return reihenfolge.map((id) => {
    const std = STANDARD_GRUPPEN.find((g) => g.id === id)!;
    const gewuenscht = (e.eintragReihenfolge?.[id] ?? []).filter((x) => std.eintraege.some((s) => s.id === x));
    const ids = [...new Set([...gewuenscht, ...std.eintraege.map((s) => s.id)])];
    return {
      id,
      titel: e.titel?.[id] ?? std.titel,
      eingeklappt: (e.eingeklappt ?? []).includes(id),
      eintraege: ids.map((x) => ({ eintrag: ALLE_EINTRAEGE.get(x)!, versteckt: (e.versteckt ?? []).includes(x), favorit: (e.favoriten ?? []).includes(x) })),
    };
  });
}

/** Die angehefteten Eintraege in der Reihenfolge der Gruppen (ohne Ausgeblendete). */
export function favoritenListe(e: SeitenleistenEinstellung): NavEintrag[] {
  return aufloesen(e).flatMap((g) => g.eintraege).filter((x) => x.favorit && !x.versteckt).map((x) => x.eintrag);
}

const umschalten = <T,>(liste: readonly T[] | undefined, wert: T): T[] => {
  const l = liste ?? [];
  return l.includes(wert) ? l.filter((x) => x !== wert) : [...l, wert];
};

export const toggleVersteckt = (e: SeitenleistenEinstellung, id: string): SeitenleistenEinstellung => ({ ...e, versteckt: umschalten(e.versteckt, id) });
export const toggleFavorit = (e: SeitenleistenEinstellung, id: string): SeitenleistenEinstellung => ({ ...e, favoriten: umschalten(e.favoriten, id) });
export const toggleEingeklappt = (e: SeitenleistenEinstellung, id: GruppenId): SeitenleistenEinstellung => ({ ...e, eingeklappt: umschalten(e.eingeklappt, id) });
export const setSchmal = (e: SeitenleistenEinstellung, schmal: boolean): SeitenleistenEinstellung => ({ ...e, schmal });

/** Gruppe umbenennen; leer = Standardname. */
export function benenneGruppe(e: SeitenleistenEinstellung, id: GruppenId, name: string): SeitenleistenEinstellung {
  const titel = { ...(e.titel ?? {}) };
  const n = name.trim().slice(0, 40);
  if (n) titel[id] = n; else delete titel[id];
  return { ...e, titel };
}

const verschiebe = <T,>(arr: readonly T[], von: number, nach: number): T[] => {
  if (nach < 0 || nach >= arr.length || von === nach) return [...arr];
  const kopie = [...arr];
  const [x] = kopie.splice(von, 1);
  kopie.splice(nach, 0, x!);
  return kopie;
};

/** Gruppe um `schritt` Plaetze verschieben (-1 = nach oben). */
export function verschiebeGruppe(e: SeitenleistenEinstellung, id: GruppenId, schritt: number): SeitenleistenEinstellung {
  const ids = aufloesen(e).map((g) => g.id);
  const i = ids.indexOf(id);
  return { ...e, gruppenReihenfolge: verschiebe(ids, i, i + schritt) };
}

/** Eintrag innerhalb seiner Gruppe verschieben. */
export function verschiebeEintrag(e: SeitenleistenEinstellung, gruppe: GruppenId, id: string, schritt: number): SeitenleistenEinstellung {
  const ids = aufloesen(e).find((g) => g.id === gruppe)!.eintraege.map((x) => x.eintrag.id);
  const i = ids.indexOf(id);
  return { ...e, eintragReihenfolge: { ...(e.eintragReihenfolge ?? {}), [gruppe]: verschiebe(ids, i, i + schritt) } };
}

export const zuruecksetzen = (): SeitenleistenEinstellung => LEERE_EINSTELLUNG;
